"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Loader, Terminal, MapPin, Phone, Mail, ChevronRight } from "lucide-react";
function TransformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
                <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center border border-primary/50">
                    <Terminal className="size-8 text-primary" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-xl font-default font-semibold text-foreground">Request Logged</h3>
                    <p className="text-muted-foreground font-default text-sm max-w-md mx-auto">
                        {successMessage}
                    </p>
                </div>
                <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg border-primary/50 hover:bg-primary/10 font-default text-xs uppercase tracking-wider">
                    <ChevronRight className="size-4 mr-1" />
                    New Quote Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Smith" className="rounded-md border-border bg-background focus-visible:border-primary focus-visible:ring-primary/20 font-default text-sm" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-default opacity-50">Primary contact for the relocation.</FormDescription>
                            <FormMessage className="font-default text-xs" />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" className="rounded-md border-border bg-background focus-visible:border-primary focus-visible:ring-primary/20 font-default text-sm" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-default opacity-50">For sending your detailed quote.</FormDescription>
                            <FormMessage className="font-default text-xs" />
                        </FormItem>} />

                <FormField control={control} name="company" rules={{
        required: "Moving from is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Moving From</FormLabel>
                            <FormControl>
                                <Input placeholder="City, State or Zip" className="rounded-md border-border bg-background focus-visible:border-primary focus-visible:ring-primary/20 font-default text-sm" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] font-default opacity-50">Origin location of your items.</FormDescription>
                            <FormMessage className="font-default text-xs" />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+1 555-000-0000" className="rounded-md border-border bg-background focus-visible:border-primary focus-visible:ring-primary/20 font-default text-sm" {...field} />
                            </FormControl>
                            <FormMessage className="font-default text-xs" />
                        </FormItem>} />
            </div>

            <FormField control={control} name="challenge" rules={{
      required: "Please describe your inventory"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default text-xs uppercase tracking-wider text-muted-foreground">Inventory & Special Requirements</FormLabel>
                        <FormControl>
                            <Textarea placeholder="List large items, number of rooms, or fragile equipment needing special care..." className="min-h-[120px] rounded-md border-border bg-background focus-visible:border-primary focus-visible:ring-primary/20 font-default text-sm resize-none" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] font-default opacity-50">Help us calculate the exact truck size and crew needed.</FormDescription>
                        <FormMessage className="font-default text-xs" />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-destructive font-default text-xs">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full relative overflow-hidden group h-14 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all border border-primary shadow-md">
                <span className="relative z-10 flex items-center justify-center gap-2 font-default font-bold uppercase tracking-widest text-sm">
                    {isSubmitting ? <>
                            <Loader className="animate-spin size-4" />
                            Calculating...
                        </> : <>
                            <ChevronRight className="size-4" />
                            Get My Quote
                        </>}
                </span>
                {}
                <div className="absolute inset-0 w-[200%] -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-scan pointer-events-none" />
            </Button>
        </Form>;
}
export default function StartYourMove() {
  return <section data-section-id="221975" id="start-your-move" className="bg-background text-foreground relative py-24 lg:py-32 overflow-hidden border-t border-border">
            {}
            <style dangerouslySetInnerHTML={{
      __html: `
                @keyframes scan {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(50%); }
                }
                .animate-scan {
                    animation: scan 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }
                .bg-industrial-grid {
                    background-image: 
                        linear-gradient(to right, hsl(var(--border) / 0.4) 1px, transparent 1px),
                        linear-gradient(to bottom, hsl(var(--border) / 0.4) 1px, transparent 1px);
                    background-size: 40px 40px;
                }
            `
    }} />

            {}
            <div className="absolute inset-0 bg-industrial-grid pointer-events-none opacity-30" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 blur-[120px] rounded-lg pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10 max-w-6xl">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
                    
                    {}
                    <div className="lg:col-span-8">
                        <div className="mb-8 space-y-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1 border border-primary/30 bg-primary/10 text-primary font-default text-xs uppercase tracking-wider font-semibold rounded-md">
                                <span className="size-1.5 bg-primary rounded-lg animate-pulse" />
                                Logistics Ready
                            </div>
                            <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-tight uppercase">
                                Start Your <span className="text-primary">Shift</span>
                            </h2>
                            <p className="text-muted-foreground font-default text-lg max-w-xl">
                                Ready for a stress-free relocation? Provide your move details below and our logistics team will generate a precise, transparent quote.
                            </p>
                        </div>

                        <div className="backdrop-blur-sm bg-card border border-border p-6 md:p-8 shadow-xl relative rounded-lg">
                            {}
                            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-primary/40" />
                            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-primary/40" />
                            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary/40" />
                            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary/40" />

                            <FormProvider formKey="start_move_form_get_quote" formId="start_move_form_get_quote" sectionName="start-your-move" defaultValues={{
              name: "",
              email: "",
              company: "",
              phone: "",
              challenge: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              company: {
                required: true
              },
              challenge: {
                required: true
              }
            }} submitText="Get My Quote" successMessage="Logistics request received. A SHIFT coordinator will contact you shortly with your personalized plan.">
                                <TransformationForm />
                            </FormProvider>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-4 lg:pt-32">
                        <div className="flex flex-col gap-8 p-6 border-l border-border relative">
                            <div className="absolute top-0 left-0 w-px h-12 bg-gradient-to-b from-primary to-transparent" />
                            
                            <div className="space-y-4">
                                <h3 className="font-default text-xs text-primary font-bold uppercase tracking-widest flex items-center gap-2">
                                    <Terminal className="size-3" />
                                    Direct Dispatch
                                </h3>
                                <p className="text-sm text-muted-foreground leading-relaxed font-default">
                                    Need an immediate response for a last-minute move? Contact our dispatch center directly for priority scheduling.
                                </p>
                            </div>

                            <div className="space-y-6 font-default text-sm">
                                <div className="group">
                                    <div className="text-muted-foreground text-xs font-bold uppercase tracking-wider mb-1 opacity-60">Headquarters</div>
                                    <div className="flex items-start gap-3 text-foreground">
                                        <MapPin className="size-4 mt-0.5 text-primary shrink-0" />
                                        <span>123 Logistics Way,<br />Metro Hub, MH 90210</span>
                                    </div>
                                </div>

                                <div className="group">
                                    <div className="text-muted-foreground text-xs font-bold uppercase tracking-wider mb-1 opacity-60">Phone Support</div>
                                    <Link to="tel:+15559876543" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors">
                                        <Phone className="size-4 text-primary shrink-0" />
                                        <span>+1 555-987-6543</span>
                                    </Link>
                                </div>

                                <div className="group">
                                    <div className="text-muted-foreground text-xs font-bold uppercase tracking-wider mb-1 opacity-60">Email Inquiries</div>
                                    <Link to="mailto:hello@shift-moves.com" className="flex items-center gap-3 text-foreground hover:text-primary transition-colors break-all">
                                        <Mail className="size-4 text-primary shrink-0" />
                                        <span>hello@shift-moves.com</span>
                                    </Link>
                                </div>
                            </div>

                            <div className="mt-4 pt-6 border-t border-border">
                                <div className="text-xs font-default font-semibold text-muted-foreground flex items-center gap-2">
                                    <span className="relative flex size-2">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                                      <span className="relative inline-flex rounded-lg size-2 bg-primary"></span>
                                    </span>
                                    Dispatch Active: 24/7 Support
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}